import { v4 as uuid } from 'uuid';

/**
 * A user-added line break inside a single translated caption phrase.
 *
 * The break sits within the phrase identified by `phraseOriginalIndices`
 * (the phrase's source-word indices in the original transcript). Time
 * placement is *not* persisted — the split timestamp is derived at
 * render / clip-emit time from `textPosition` via the active
 * `SplitStrategy` (see `karaokeWordTiming.ts`), so source-word timing
 * edits and strategy changes scale the split automatically.
 *
 * Synthetic breaks never cross phrase boundaries — multi-phrase
 * captions are already segmented at natural phrase boundaries by the
 * producer (PLAT-9621); synthetic splits are a strictly intra-phrase
 * refinement.
 */
export interface CaptionLineBreak {
  id: string;
  phraseOriginalIndices: number[];
  textPosition: number;
  isSynthetic: true;
}

export interface CreateCaptionLineBreakArgs {
  phraseOriginalIndices: number[];
  textPosition: number;
  id?: string;
}

export function createCaptionLineBreak(
  args: CreateCaptionLineBreakArgs,
): CaptionLineBreak {
  if (!Number.isFinite(args.textPosition) || args.textPosition <= 0) {
    throw new Error(
      `CaptionLineBreak.textPosition must be > 0; got ${args.textPosition}`,
    );
  }
  if (args.phraseOriginalIndices.length === 0) {
    throw new Error(
      'CaptionLineBreak.phraseOriginalIndices must be non-empty',
    );
  }
  return {
    id: args.id ?? uuid(),
    phraseOriginalIndices: [...args.phraseOriginalIndices],
    textPosition: args.textPosition,
    isSynthetic: true,
  };
}

export function sameOriginalIndices(
  a: readonly number[],
  b: readonly number[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
